import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Chip,
    List,
    ListItem,
    Skeleton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
} from "@mui/material";
import { TrendingDown } from '@mui/icons-material';
import { api } from '../../../../api/api';
import { useAuth } from '../../../../contexts/AuthProvider/AuthProvider';
import { generateYearOptions, MONTHS } from '../../../Reports/ReportHelpers';
import { useBillingCompanyContext } from '../../../../providers';

const COLORS = ["#0d3b66", "#1e5a8e", "#2e7ab8", "#4a9fd8", "#6bb8e8"];

interface CancellationReasonData {
    cancellation_reason: string;
    event_count: number;
    lost_revenue: string;
}

export interface CancelationReasonReportProps {
    frameless?: boolean;
}

export const CancelationReasonReport: React.FC<CancelationReasonReportProps> = ({ frameless = false }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const { getActiveAuth } = useAuth();
    const activeAuth = getActiveAuth();
    const { comapny: { company_outlet_uuid} } = useBillingCompanyContext();

    // State management
    const currentDate = new Date();
    const [commonYear, setCommonYear] = useState<number>(currentDate.getFullYear());
    const [commonMonth, setCommonMonth] = useState<number | "">(currentDate.getMonth() + 1);
    const [cancellationReasons, setCancellationReasons] = useState<CancellationReasonData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // Fetch data from API
    const fetchCancellationReasons = async () => {
        try {
            setLoading(true);

            // Calculate date range
            let fromDate: string;
            let toDate: string;

            if (commonMonth === "") {
                // All months - full year
                fromDate = `${commonYear}-01-01`;
                toDate = `${commonYear}-12-31`;
            } else {
                // Specific month
                const year = commonYear;
                const month = commonMonth;
                const lastDay = new Date(year, month, 0).getDate();
                fromDate = `${year}-${String(month).padStart(2, '0')}-01`;
                toDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
            }

            const response = await api.get('/report/get-cancellation-reason-list', {
                params: {
                    company_outlet_uuid: company_outlet_uuid,
                    from_date: fromDate,
                    to_date: toDate,
                },
            });

            if (response.data.data && Array.isArray(response.data.data)) {
                setCancellationReasons(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching cancellation reasons:", error);
            setCancellationReasons([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on mount and when filters change
    useEffect(() => {
        if (company_outlet_uuid) {
            fetchCancellationReasons();
        }
    }, [commonYear, commonMonth, company_outlet_uuid]);

    const selectSx = frameless
        ? {
            borderRadius: "12px",
            fontSize: "0.82rem",
            fontWeight: 600,
            backgroundColor: isDark ? "rgba(30,41,59,0.5)" : "#ffffff",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
            },
        }
        : {};

    // --- DESKTOP VIEW (Original untouched layout & design for big screens) ---
    if (!frameless) {
        return (
            <Box
                sx={{
                    gridColumn: {
                        xs: "span 2",
                        md: "span 4",
                        lg: "span 3",
                    },
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    p: 2.5,
                    mt: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "flex-start", md: "center" },
                        justifyContent: "space-between",
                        gap: { xs: 1.5, md: 0 },
                        mb: 2,
                    }}
                >
                    <Typography fontWeight="bold" variant="h3">
                        Why people cancel
                    </Typography>

                    {/* Filters */}
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: 2, width: { xs: "100%", md: "auto" } }}>
                        <FormControl size="small" sx={{ flex: { xs: 1, md: "none" }, minWidth: { xs: 0, md: 120 } }}>
                            <InputLabel>Year</InputLabel>
                            <Select
                                value={commonYear}
                                label="Year"
                                onChange={(e) => setCommonYear(Number(e.target.value))}
                                disabled={loading}
                            >
                                {generateYearOptions().map((y) => (
                                    <MenuItem key={y} value={y}>
                                        {y}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: { xs: 1, md: "none" }, minWidth: { xs: 0, md: 120 } }}>
                            <InputLabel>Month</InputLabel>
                            <Select
                                value={commonMonth}
                                label="Month"
                                onChange={(e) => setCommonMonth(e.target.value === "" ? "" : Number(e.target.value))}
                                disabled={loading}
                                renderValue={(value) => {
                                    if (!value) return "All Months";
                                    const month = MONTHS.find((m) => m.value === value);
                                    return month ? month.label : "";
                                }}
                            >
                                <MenuItem value="">All Months</MenuItem>
                                {MONTHS.map((m: { value: number; label: string }) => (
                                    <MenuItem key={m.value} value={m.value}>
                                        {m.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Loading State */}
                {loading ? (
                    <List
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            p: 0,
                        }}
                    >
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton
                                key={i}
                                variant="rectangular"
                                height={48}
                                sx={{ borderRadius: "16px" }}
                            />
                        ))}
                    </List>
                ) : cancellationReasons.filter((c) => c.event_count > 0).length === 0 ? (
                    // Empty State
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 150,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            No cancellations for the selected period
                        </Typography>
                    </Box>
                ) : (
                    // Data List
                    <List
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            p: 0,
                        }}
                    >
                        {cancellationReasons
                            .filter((c) => c.event_count > 0)
                            .map((c, i) => {
                                return (
                                    <ListItem
                                        key={c.cancellation_reason}
                                        disablePadding
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            borderRadius: "16px",
                                            backgroundColor: "#eae9e999",
                                            px: 2,
                                            py: 1.5,
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                backgroundColor: "rgba(255,255,255,0.8)",
                                                transform: "translateX(4px)",
                                            },
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                fontSize={14}
                                                fontWeight={600}
                                            >
                                                {c.cancellation_reason}
                                            </Typography>
                                            {Number(c.lost_revenue) > 0 && (
                                                <Typography
                                                    fontSize={12}
                                                >
                                                    Lost: ₹{Number(c.lost_revenue).toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Box
                                            sx={{
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: "999px",
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                                color: "#fff",
                                                background: COLORS[i % COLORS.length],
                                                minWidth: 32,
                                                textAlign: "center",
                                            }}
                                        >
                                            {c.event_count}
                                        </Box>
                                    </ListItem>
                                );
                            })}
                    </List>
                )}
            </Box>
        );
    }

    // --- MOBILE VIEW (Frameless, sleek mobile dashboard design) ---
    return (
        <Box
            sx={{
                width: "100%",
                borderRadius: 0,
                backgroundColor: "transparent",
                boxShadow: "none",
                border: "none",
                p: 0,
                mt: 0,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "row", md: "row" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    mb: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        fontSize: "1.15rem",
                        color: theme.palette.text.primary,
                        letterSpacing: "-0.01em",
                    }}
                >
                    Why people cancel
                </Typography>

                {/* Filters */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel id="cancel-year-label" sx={{ fontSize: "0.8rem" }}>Year</InputLabel>
                        <Select
                            labelId="cancel-year-label"
                            value={commonYear}
                            label="Year"
                            onChange={(e) => setCommonYear(Number(e.target.value))}
                            disabled={loading}
                            sx={selectSx}
                        >
                            {generateYearOptions().map((y) => (
                                <MenuItem key={y} value={y} sx={{ fontSize: "0.82rem" }}>
                                    {y}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel id="cancel-month-label" sx={{ fontSize: "0.8rem" }}>Month</InputLabel>
                        <Select
                            labelId="cancel-month-label"
                            value={commonMonth}
                            label="Month"
                            onChange={(e) => setCommonMonth(e.target.value === "" ? "" : Number(e.target.value))}
                            disabled={loading}
                            sx={selectSx}
                            renderValue={(value) => {
                                if (!value) return "All";
                                const month = MONTHS.find((m) => m.value === value);
                                return month ? month.label.substring(0, 3) : "";
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: "0.82rem" }}>All Months</MenuItem>
                            {MONTHS.map((m: { value: number; label: string }) => (
                                <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.82rem" }}>
                                    {m.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Loading State */}
            {loading ? (
                <List
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                        p: 0,
                    }}
                >
                    {[1, 2, 3].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rectangular"
                            height={54}
                            sx={{ borderRadius: "16px" }}
                        />
                    ))}
                </List>
            ) : cancellationReasons.filter((c) => c.event_count > 0).length === 0 ? (
                // Empty State
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 140,
                        backgroundColor: isDark ? "rgba(30, 41, 59, 0.5)" : "#ffffff",
                        borderRadius: "16px",
                        border: `1px dashed ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                        p: 3,
                    }}
                >
                    <TrendingDown sx={{ fontSize: 28, color: "text.disabled", mb: 1, opacity: 0.6 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        No cancellations for the selected period
                    </Typography>
                </Box>
            ) : (
                // Data List
                <List
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.25,
                        p: 0,
                    }}
                >
                    {cancellationReasons
                        .filter((c) => c.event_count > 0)
                        .map((c, i) => {
                            return (
                                <ListItem
                                    key={c.cancellation_reason}
                                    disablePadding
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        borderRadius: "16px",
                                        backgroundColor: isDark ? "rgba(30, 41, 59, 0.7)" : "#ffffff",
                                        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                                        boxShadow: isDark
                                            ? "0 4px 16px rgba(0,0,0,0.2)"
                                            : "0 2px 10px rgba(0,0,0,0.03)",
                                        px: 2,
                                        py: 1.5,
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                            transform: "translateY(-1px)",
                                            boxShadow: isDark
                                                ? "0 6px 20px rgba(0,0,0,0.3)"
                                                : "0 4px 14px rgba(0,0,0,0.06)",
                                        },
                                    }}
                                >
                                    <Box sx={{ flex: 1, pr: 1.5 }}>
                                        <Typography
                                            fontSize="0.88rem"
                                            fontWeight={600}
                                            sx={{ color: theme.palette.text.primary, lineHeight: 1.3 }}
                                        >
                                            {c.cancellation_reason}
                                        </Typography>
                                        {Number(c.lost_revenue) > 0 && (
                                            <Typography
                                                fontSize="0.75rem"
                                                fontWeight={500}
                                                sx={{ color: isDark ? "#f87171" : "#e46a76", mt: 0.25 }}
                                            >
                                                Lost Revenue: ₹{Number(c.lost_revenue).toLocaleString()}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box
                                        sx={{
                                            px: 1.5,
                                            py: 0.4,
                                            borderRadius: "999px",
                                            fontSize: "0.75rem",
                                            fontWeight: 700,
                                            color: "#fff",
                                            background: COLORS[i % COLORS.length],
                                            minWidth: 32,
                                            textAlign: "center",
                                            boxShadow: `0 2px 8px ${COLORS[i % COLORS.length]}40`,
                                        }}
                                    >
                                        {c.event_count}
                                    </Box>
                                </ListItem>
                            );
                        })}
                </List>
            )}
        </Box>
    );
};

